import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, apiGet, apiPost } from "@/contexts/AuthContext";
import { Nav } from "@/pages/Dashboard";
import { toast } from "sonner";
import { MessageCircle, Send, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommunityPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [weekFilter, setWeekFilter] = useState(null);
  const [posting, setPosting] = useState(false);
  const [expandedPost, setExpandedPost] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [replying, setReplying] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const loadPosts = async () => {
    try {
      const url = weekFilter ? `${API}/community/posts?week_num=${weekFilter}` : `${API}/community/posts`;
      const { data } = await apiGet(url);
      setPosts(data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadPosts(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekFilter]);

  const handlePost = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    setPosting(true);
    try {
      await apiPost(`${API}/community/posts`, { title, content, week_num: weekFilter });
      toast.success("Post shared with the group.");
      setTitle(""); setContent(""); setShowForm(false);
      loadPosts();
    } catch { toast.error("Could not create post."); }
    setPosting(false);
  };

  const handleReply = async (postId) => {
    if (!replyContent.trim()) return;
    setReplying(true);
    try {
      await apiPost(`${API}/community/posts/${postId}/replies`, { content: replyContent });
      toast.success("Reply posted.");
      setReplyContent("");
      loadPosts();
    } catch { toast.error("Could not post reply."); }
    setReplying(false);
  };

  return (
    <div className="min-h-screen" style={{ background: '#F7F5F0' }}>
      <Nav />
      <main className="px-6 sm:px-12 py-10 max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 fade-in">
          <div>
            <h1 className="text-4xl" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Community</h1>
            <p className="text-sm mt-1" style={{ color: '#5B665E' }}>Share, reflect, and connect with your cohort.</p>
          </div>
          <button onClick={() => setShowForm(!showForm)} className="earthy-btn earthy-btn-primary text-sm" data-testid="new-post-toggle">
            {showForm ? 'Cancel' : 'New Post'}
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-8 fade-in stagger-1" data-testid="community-filters">
          <button onClick={() => setWeekFilter(null)} className={`earthy-badge cursor-pointer ${!weekFilter ? 'badge-active' : 'badge-locked'}`} data-testid="filter-all">All</button>
          {[1, 2, 3, 4].map(w => (
            <button key={w} onClick={() => setWeekFilter(w)} className={`earthy-badge cursor-pointer ${weekFilter === w ? 'badge-active' : 'badge-locked'}`} data-testid={`filter-week-${w}`}>
              Week {w}
            </button>
          ))}
        </div>

        {/* New Post Form */}
        {showForm && (
          <form onSubmit={handlePost} className="earthy-card mb-8 fade-in" data-testid="new-post-form">
            <h3 className="text-xl mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Share with the Group</h3>
            <input value={title} onChange={e => setTitle(e.target.value)} className="earthy-input mb-3" placeholder="Give your post a title..." required data-testid="post-title-input" />
            <textarea value={content} onChange={e => setContent(e.target.value)} className="earthy-input min-h-[100px] mb-4 resize-y" placeholder="What's on your mind?" required data-testid="post-content-input" />
            <button type="submit" disabled={posting} className="earthy-btn earthy-btn-primary text-sm flex items-center gap-1.5" data-testid="submit-post-btn">
              <Send size={14} strokeWidth={1.5} /> {posting ? "Posting..." : "Share"}
            </button>
          </form>
        )}

        {/* Posts */}
        {posts.length === 0 ? (
          <div className="earthy-card text-center py-16" data-testid="no-posts">
            <MessageCircle size={32} strokeWidth={1.5} className="mx-auto mb-4" style={{ color: '#5B665E' }} />
            <p className="text-xl mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>No posts yet</p>
            <p className="text-sm" style={{ color: '#5B665E' }}>Be the first to start a conversation.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((post) => (
              <div key={post.post_id} className="earthy-card fade-in" data-testid={`post-${post.post_id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-lg font-medium" style={{ color: '#2D3A31' }}>{post.title}</h3>
                    <p className="text-xs" style={{ color: '#5B665E' }}>
                      {post.user_name} {post.week_num && <span className="earthy-badge badge-active text-[9px] ml-1">Week {post.week_num}</span>}
                    </p>
                  </div>
                  <button onClick={() => setExpandedPost(expandedPost === post.post_id ? null : post.post_id)} className="p-1 rounded hover:bg-[#EAE6DF] transition-colors" data-testid={`toggle-post-${post.post_id}`}>
                    {expandedPost === post.post_id ? <ChevronUp size={18} strokeWidth={1.5} /> : <ChevronDown size={18} strokeWidth={1.5} />}
                  </button>
                </div>
                <p className="text-sm leading-relaxed mb-3" style={{ color: '#5B665E' }}>{post.content}</p>
                
                {expandedPost === post.post_id && (
                  <div className="mt-4 pt-4" style={{ borderTop: '1px solid #EAE6DF' }}>
                    {post.replies?.length > 0 && (
                      <div className="space-y-3 mb-4">
                        {post.replies.map((r) => (
                          <div key={r.reply_id} className="pl-4" style={{ borderLeft: '2px solid #D1CCC0' }}>
                            <p className="text-sm" style={{ color: '#5B665E' }}>{r.content}</p>
                            <p className="text-xs mt-1" style={{ color: '#5B665E', opacity: 0.7 }}>{r.user_name}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input value={replyContent} onChange={e => setReplyContent(e.target.value)} className="earthy-input text-sm flex-1" placeholder="Write a reply..." data-testid={`reply-input-${post.post_id}`} />
                      <button onClick={() => handleReply(post.post_id)} disabled={replying || !replyContent.trim()} className="earthy-btn earthy-btn-accent text-xs py-2 px-3" data-testid={`reply-btn-${post.post_id}`}>
                        <Send size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-2 text-xs mt-2" style={{ color: '#5B665E' }}>
                  <MessageCircle size={12} strokeWidth={1.5} /> {post.replies?.length || 0} replies
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
